import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "page" }
const _hoisted_2 = {
  key: 2,
  class: "section content-container"
}
const _hoisted_3 = { class: "container is-fluid" }
const _hoisted_4 = { class: "columns" }
const _hoisted_5 = { class: "column is-8" }
const _hoisted_6 = { class: "columns" }
const _hoisted_7 = { class: "column is-6 columns__column--no-top-padding" }
const _hoisted_8 = { class: "field" }
const _hoisted_9 = { class: "field" }
const _hoisted_10 = { class: "field" }
const _hoisted_11 = { class: "field" }
const _hoisted_12 = { class: "field" }
const _hoisted_13 = { class: "columns" }
const _hoisted_14 = { class: "column is-6" }
const _hoisted_15 = { class: "field" }
const _hoisted_16 = { class: "field" }
const _hoisted_17 = { class: "columns" }
const _hoisted_18 = { class: "column is-6" }
const _hoisted_19 = { class: "field" }
const _hoisted_20 = { class: "columns" }
const _hoisted_21 = { class: "column" }
const _hoisted_22 = { class: "field" }
const _hoisted_23 = { class: "field" }
const _hoisted_24 = { class: "columns" }
const _hoisted_25 = { class: "column is-6" }
const _hoisted_26 = { class: "field" }
const _hoisted_27 = { class: "column is-4 preview-column" }
const _hoisted_28 = { class: "preview-container" }
const _hoisted_29 = { class: "view-toggle-container" }

import WebAssistantPreview from '../components/WebAssistantPreview.vue';
import WebAssistantViewToggle from '../components/WebAssistantViewToggle.vue';
import ImageUpload from '../components/WebAssistantImageUpload.vue';
import AssetService from '../services/assets';
import { webAssistantApi } from '../services/webAssistant';
import { webAssistantAppService } from '../services/webAssistantAppService';
import { HomescreenAppInitViewModel, HomescreenAppViewModel, WebAssistantViewModel } from '../models';
import { HANDLE_SERIOUS_ERROR } from '@/store/app/actionTypes';
import Toast from '@/shared/services/toast';
import Banner from '@/shared/components/Banner.vue';
import CollapsibleSection from '@/shared/components/CollapsibleSection.vue';
import CapableAppPicker from '@/shared/components/capableAppPicker/CapableAppPicker.vue';
import PageHeader from '@/shared/components/pageHeader/PageHeader.vue';
import ReadonlyCopyableField from '@/shared/components/ReadonlyCopyableField.vue';
import { HomescreenAppCache } from '../services/homescreenAppCache';
import TagsInput from '@/shared/components/TagsInput.vue';
import { TypicalResponseTimeOptions } from '@/features/webassistant/models';
import { ConversationMessage } from '../models/conversation-message';
import { MessageDirection } from '../models/messageDirection';
import { MessageStatus } from '../models/messageStatus';
import { useStore } from 'vuex';
import { computed, onMounted, ref } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import { ConfiguredAppIntegration } from '@/features/apps/shared/models/configuredAppIntegration';
import VuelidateUtil from '@shared/util/vuelidateUtil';


export default /*@__PURE__*/_defineComponent({
  __name: 'WebAssistant',
  setup(__props) {

const store = useStore();
const currentWorkspace = computed(() => store.getters.currentWorkspace);
const handleSeriousError = (error: string) => store.dispatch(HANDLE_SERIOUS_ERROR, error);

const viewModel = ref<WebAssistantViewModel>({
  theme: {
    brandColour: '#062B80',
    sentMessageColour: '#062B80',
    actionColour: '#062B80',
  },
  headerText: '',
  typicalResponseTime: 0,
  whitelistedDomains: [],
  homescreenApps: [],
  imageUrl: '',
  greetingText: 'Hi there 👋',
  introductionText: "We're here to help. Ask us anything",
  useDefaultLauncher: true,
});

const rules = {
  greetingText: { required, maxLength: maxLength(25) },
  introductionText: { required, maxLength: maxLength(40) },
};
const $v = useVuelidate(rules, viewModel);

const activeCollapseId = ref(1);
const identityVerificationSecret = ref('');
const initialisedHomescreenApps = ref<HomescreenAppInitViewModel[]>([]);
const imageUploading = ref(false);
const webAssistantAppLoaded = ref(false);
const webAssistantAppInstallError = ref(false);
const reinstallInProgress = ref(false);
const updateInProgress = ref(false);
const activeView = ref<'homescreen' | 'conversation'>('homescreen');
const codeSnippet = ref('');
const homescreenAppCache = ref<HomescreenAppCache>();

const typicalResponseTimeOptions = TypicalResponseTimeOptions;
const sampleMessages: ConversationMessage[] = [
  {
    id: '1',
    type: 'text',
    direction: MessageDirection.Inbound,
    status: MessageStatus.DeliverySucceeded,
    timestampUtc: new Date(),
    text: 'Hi there! Is there anything we can help you with?',
  },
  {
    id: '2',
    type: 'text',
    direction: MessageDirection.Outbound,
    status: MessageStatus.DeliverySucceeded,
    timestampUtc: new Date(),
    text: 'Hi, yes there is...',
  },
];

onMounted(async () => {
  if (!currentWorkspace.value) {
    await handleSeriousError('No workspace selected');
    return;
  }

  homescreenAppCache.value = new HomescreenAppCache();

  try {
    const response = await webAssistantApi.getInstalledApp(currentWorkspace.value.id);
    viewModel.value = response as WebAssistantViewModel;
    homescreenAppsChanged(viewModel.value.homescreenApps);
    identityVerificationSecret.value = response.identityVerificationSecret;
    webAssistantAppLoaded.value = true;
  } catch (error: any) {
    if (error.status === 404) {
      webAssistantAppInstallError.value = true;
      viewModel.value.headerText = currentWorkspace.value.name;
      webAssistantAppLoaded.value = true;
      return;
    }
    await handleSeriousError(error.message);
  }

  const data: any = await AssetService.loadCodeSnippet();
  codeSnippet.value = data.replace('<workspace-id>', currentWorkspace.value.id);
});

// Generate an error message for an input

const greetingErrorMessage = computed(() => VuelidateUtil.getErrorMessage($v, 'greetingText', 'Greeting text'));
const introductionErrorMessage = computed(() =>
  VuelidateUtil.getErrorMessage($v, 'introductionText', 'Introduction text'),
);

function reinstall() {
  if (reinstallInProgress.value) {
    return;
  }

  reinstallInProgress.value = true;

  webAssistantAppService
    .fullInstall(currentWorkspace.value.id, currentWorkspace.value.name, viewModel.value)
    .then((response) => {
      viewModel.value = response as WebAssistantViewModel;
      identityVerificationSecret.value = response.identityVerificationSecret;
      Toast.showSuccess('Web Assistant has been successfully configured');
      webAssistantAppInstallError.value = false;
    })
    .catch((error) => {
      Toast.showError(error.message);
    })
    .finally(() => {
      reinstallInProgress.value = false;
    });
}

function update() {
  if ($v.value.$invalid) {
    return;
  }

  if (imageUploading.value) {
    Toast.showInfo('Cannot save while an image is being uploaded, please try again');
    return;
  }

  if (!updateInProgress.value && !reinstallInProgress.value) {
    updateInProgress.value = true;

    webAssistantApi
      .updateWebAssistant(currentWorkspace.value.id, viewModel.value)
      .then(() => Toast.showSuccess('Successfully updated Web Assistant'))
      .catch((err) => Toast.showError(err.message))
      .finally(() => (updateInProgress.value = false));
  }
}

function onImageSelected() {
  imageUploading.value = true;
}

function onImageUploaded(imageUrl: string) {
  viewModel.value.imageUrl = imageUrl;
  imageUploading.value = false;
}

function onImageCleared() {
  viewModel.value.imageUrl = '';
  imageUploading.value = false;
}

function getCodeSnippet() {
  return '<script>' + codeSnippet.value + '<\/script>';
}

function getInitSnippet() {
  return `<script>Connect.init({ workspaceId: '${currentWorkspace.value.id}' });<\/script>`;
}

function configRequest(workspaceId: string, installedAppId: string, inputData: any, storedData: any, componentId: any) {
  return webAssistantApi.homescreenAppConfig(workspaceId, installedAppId, inputData, storedData, componentId);
}

function homescreenAppsChanged(apps: HomescreenAppViewModel[]) {
  const promises = apps.map((app) => {
    return homescreenAppCache.value
      ?.initialiseApp(currentWorkspace.value.id, app)
      .then((response: HomescreenAppInitViewModel) => {
        return { configured: app, initialised: response };
      })
      .catch((error: any) => {
        Toast.showError('Failed to load homescreen app');
        const init: HomescreenAppInitViewModel = {
          id: `${app.installedAppId}`,
          card: {
            components: [],
          },
        };
        return { configured: app, initialised: init };
      });
  });

  Promise.all(promises).then((res) => {
    viewModel.value.homescreenApps = res.map(
      (x: { configured: HomescreenAppViewModel; initialised: HomescreenAppInitViewModel }) => x!.configured,
    );
    initialisedHomescreenApps.value = res.map(
      (x: { configured: HomescreenAppViewModel; initialised: HomescreenAppInitViewModel }) => x!.initialised,
    );
  });
}

function messageLoad($state: any) {
  $state.loaded();
  $state.complete();
}

function handleAppUpdate(app: HomescreenAppViewModel, appIndex: number) {
  viewModel.value.homescreenApps[appIndex] = app;
  homescreenAppsChanged(viewModel.value.homescreenApps);
}

// Whether an input is currently invalid because it is required and empty.
function invalidRequired($v: any, prop: string) {
  return !$v.viewModel[prop].required && $v.viewModel[prop].$invalid && $v.viewModel[prop].$dirty;
}

// Whether an input is currently invalid because it is longer than the maximum allowed.
function invalidMaxLength($v: any, prop: string) {
  return !$v.viewModel[prop].maxLength && $v.viewModel[prop].$invalid && $v.viewModel[prop].$dirty;
}

return (_ctx: any,_cache: any) => {
  const _component_n_button = _resolveComponent("n-button")!
  const _component_n_loading_spinner = _resolveComponent("n-loading-spinner")!
  const _component_n_colour_picker = _resolveComponent("n-colour-picker")!
  const _component_n_toggle = _resolveComponent("n-toggle")!
  const _component_n_text_input = _resolveComponent("n-text-input")!
  const _component_n_radio_buttons = _resolveComponent("n-radio-buttons")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (webAssistantAppInstallError.value)
      ? (_openBlock(), _createBlock(Banner, {
          key: 0,
          "show-overlay": true
        }, {
          default: _withCtx(() => [
            _cache[22] || (_cache[22] = _createElementVNode("div", { class: "banner-text" }, "There's a problem with your Web Assistant config. Let's set it up again", -1)),
            _createVNode(_component_n_button, {
              onClick: reinstall,
              variant: "primary",
              loading: reinstallInProgress.value
            }, {
              default: _withCtx(() => _cache[21] || (_cache[21] = [
                _createTextVNode(" Reconfigure ")
              ])),
              _: 1
            }, 8, ["loading"])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(PageHeader, {
      title: "Web Assistant",
      description: "Support your customers easily and effectively with a customizable web assistant"
    }),
    (!webAssistantAppLoaded.value)
      ? (_openBlock(), _createBlock(_component_n_loading_spinner, { key: 1 }))
      : _createCommentVNode("", true),
    (webAssistantAppLoaded.value)
      ? (_openBlock(), _createElementBlock("section", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(CollapsibleSection, {
                  index: 1,
                  modelValue: activeCollapseId.value,
                  "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((activeCollapseId).value = $event))
                }, {
                  title: _withCtx(() => _cache[23] || (_cache[23] = [
                    _createTextVNode("Style your widget")
                  ])),
                  subtitle: _withCtx(() => _cache[24] || (_cache[24] = [
                    _createTextVNode("Style the web assistant widget to match your site")
                  ])),
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_6, [
                      _createElementVNode("div", _hoisted_7, [
                        _cache[26] || (_cache[26] = _createElementVNode("strong", { class: "label" }, "Colours", -1)),
                        _createElementVNode("div", _hoisted_8, [
                          _createVNode(_component_n_colour_picker, {
                            id: "brand-colour",
                            modelValue: viewModel.value.theme.brandColour,
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((viewModel.value.theme.brandColour) = $event)),
                            label: "Brand Colour"
                          }, null, 8, ["modelValue"])
                        ]),
                        _createElementVNode("div", _hoisted_9, [
                          _createVNode(_component_n_colour_picker, {
                            id: "sent-message-colour",
                            modelValue: viewModel.value.theme.sentMessageColour,
                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((viewModel.value.theme.sentMessageColour) = $event)),
                            label: "Sent Message Colour"
                          }, null, 8, ["modelValue"])
                        ]),
                        _createElementVNode("div", _hoisted_10, [
                          _createVNode(_component_n_colour_picker, {
                            id: "action-colour",
                            modelValue: viewModel.value.theme.actionColour,
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((viewModel.value.theme.actionColour) = $event)),
                            label: "Action Colour"
                          }, null, 8, ["modelValue"])
                        ]),
                        _cache[27] || (_cache[27] = _createElementVNode("strong", { class: "label" }, "Logo", -1)),
                        _createElementVNode("div", _hoisted_11, [
                          _cache[25] || (_cache[25] = _createElementVNode("label", { class: "label" }, "Upload a logo to display on the homescreen", -1)),
                          _createVNode(ImageUpload, {
                            accept: '.png,.jpg,.jpeg',
                            previewWidth: 300,
                            previewHeight: 48,
                            image: viewModel.value.imageUrl,
                            disabled: webAssistantAppInstallError.value,
                            onSelected: _cache[3] || (_cache[3] = ($event: any) => (onImageSelected())),
                            onUploaded: _cache[4] || (_cache[4] = ($event: any) => (onImageUploaded($event))),
                            onCleared: _cache[5] || (_cache[5] = ($event: any) => (onImageCleared()))
                          }, null, 8, ["image", "disabled"])
                        ]),
                        _cache[28] || (_cache[28] = _createElementVNode("strong", { class: "label" }, "Launcher", -1)),
                        _createElementVNode("div", _hoisted_12, [
                          _createVNode(_component_n_toggle, {
                            id: "use-default-launcher",
                            name: "use-default-launcher",
                            modelValue: viewModel.value.useDefaultLauncher,
                            "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((viewModel.value.useDefaultLauncher) = $event)),
                            label: "Show contacts the launcher"
                          }, null, 8, ["modelValue"])
                        ])
                      ])
                    ])
                  ]),
                  _: 1
                }, 8, ["modelValue"]),
                _createVNode(CollapsibleSection, {
                  index: 2,
                  modelValue: activeCollapseId.value,
                  "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((activeCollapseId).value = $event))
                }, {
                  title: _withCtx(() => _cache[29] || (_cache[29] = [
                    _createTextVNode("Say hello")
                  ])),
                  subtitle: _withCtx(() => _cache[30] || (_cache[30] = [
                    _createTextVNode("Greet your visitors and introduce yourself")
                  ])),
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("div", _hoisted_14, [
                        _createElementVNode("div", _hoisted_15, [
                          _createVNode(_component_n_text_input, {
                            modelValue: viewModel.value.greetingText,
                            "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((viewModel.value.greetingText) = $event)),
                            label: "Greeting",
                            id: "greeting",
                            onInput: _cache[9] || (_cache[9] = ($event: any) => (_unref($v).greetingText.$touch())),
                            "error-message": greetingErrorMessage.value
                          }, null, 8, ["modelValue", "error-message"])
                        ]),
                        _createElementVNode("div", _hoisted_16, [
                          _createVNode(_component_n_text_input, {
                            modelValue: viewModel.value.introductionText,
                            "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((viewModel.value.introductionText) = $event)),
                            label: "Introduction",
                            id: "introduction",
                            onInput: _cache[11] || (_cache[11] = ($event: any) => (_unref($v).introductionText.$touch())),
                            "error-message": introductionErrorMessage.value
                          }, null, 8, ["modelValue", "error-message"])
                        ])
                      ])
                    ])
                  ]),
                  _: 1
                }, 8, ["modelValue"]),
                _createVNode(CollapsibleSection, {
                  index: 3,
                  modelValue: activeCollapseId.value,
                  "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((activeCollapseId).value = $event))
                }, {
                  title: _withCtx(() => _cache[31] || (_cache[31] = [
                    _createTextVNode("Set your availability")
                  ])),
                  subtitle: _withCtx(() => _cache[32] || (_cache[32] = [
                    _createTextVNode("Inform your visitors how long they can expect to wait")
                  ])),
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_17, [
                      _cache[33] || (_cache[33] = _createElementVNode("div", { class: "column is-6" }, " Let your customers know how long they might be waiting for a response. This will be displayed on the homescreen, and in the conversation header ", -1)),
                      _createElementVNode("div", _hoisted_18, [
                        _createElementVNode("div", _hoisted_19, [
                          _createVNode(_component_n_radio_buttons, {
                            modelValue: viewModel.value.typicalResponseTime,
                            "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((viewModel.value.typicalResponseTime) = $event)),
                            options: _unref(typicalResponseTimeOptions)
                          }, null, 8, ["modelValue", "options"])
                        ])
                      ])
                    ])
                  ]),
                  _: 1
                }, 8, ["modelValue"]),
                _createVNode(CollapsibleSection, {
                  index: 4,
                  modelValue: activeCollapseId.value,
                  "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((activeCollapseId).value = $event))
                }, {
                  title: _withCtx(() => _cache[34] || (_cache[34] = [
                    _createTextVNode("Add homescreen apps")
                  ])),
                  subtitle: _withCtx(() => _cache[35] || (_cache[35] = [
                    _createTextVNode("Enable your visitors to self serve before they start a conversation ")
                  ])),
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_20, [
                      _createElementVNode("div", _hoisted_21, [
                        _createVNode(CapableAppPicker, {
                          modalHeading: 'Homescreen Apps',
                          capability: 'homescreen',
                          selectedApps: viewModel.value.homescreenApps,
                          configRequest: configRequest,
                          onChange: homescreenAppsChanged,
                          onUpdate: _cache[15] || (_cache[15] = (app, appIndex) => handleAppUpdate(app, appIndex))
                        }, null, 8, ["selectedApps"])
                      ])
                    ])
                  ]),
                  _: 1
                }, 8, ["modelValue"]),
                _createVNode(CollapsibleSection, {
                  index: 5,
                  modelValue: activeCollapseId.value,
                  "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((activeCollapseId).value = $event))
                }, {
                  title: _withCtx(() => _cache[36] || (_cache[36] = [
                    _createTextVNode("Embed in your website")
                  ])),
                  subtitle: _withCtx(() => _cache[37] || (_cache[37] = [
                    _createTextVNode("Connecting Web Assistant lets you chat with people when they're on your website ")
                  ])),
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_22, [
                      _cache[38] || (_cache[38] = _createElementVNode("label", { class: "label" }, "Code Snippet", -1)),
                      _createVNode(ReadonlyCopyableField, {
                        readonlyValue: getCodeSnippet(),
                        onCopyMessage: "Copied snippet to clipboard"
                      }, null, 8, ["readonlyValue"]),
                      _cache[39] || (_cache[39] = _createElementVNode("p", { class: "help" }, [
                        _createTextVNode(" Copy the snippet and add it to the "),
                        _createElementVNode("strong", null, "<head>"),
                        _createTextVNode(" section on all pages of your website ")
                      ], -1))
                    ]),
                    _createElementVNode("div", _hoisted_23, [
                      _cache[40] || (_cache[40] = _createElementVNode("label", { class: "label" }, "Init Snippet", -1)),
                      _createVNode(ReadonlyCopyableField, {
                        readonlyValue: getInitSnippet(),
                        onCopyMessage: "Copied snippet to clipboard"
                      }, null, 8, ["readonlyValue"]),
                      _cache[41] || (_cache[41] = _createElementVNode("p", { class: "help" }, [
                        _createTextVNode(" Copy the snippet and add it to the "),
                        _createElementVNode("strong", null, "<body>"),
                        _createTextVNode(" section on all pages of your website ")
                      ], -1))
                    ])
                  ]),
                  _: 1
                }, 8, ["modelValue"]),
                _createVNode(CollapsibleSection, {
                  index: 6,
                  modelValue: activeCollapseId.value,
                  "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((activeCollapseId).value = $event))
                }, {
                  title: _withCtx(() => _cache[42] || (_cache[42] = [
                    _createTextVNode("Secure your Web Assistant")
                  ])),
                  subtitle: _withCtx(() => _cache[43] || (_cache[43] = [
                    _createTextVNode("Domain whitelisting and identity verification")
                  ])),
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_24, [
                      _cache[44] || (_cache[44] = _createElementVNode("div", { class: "column is-6" }, " Enter a list of domains which will contain the web assistant widget. Domains that are not listed here will not be able to use the web assistant widget. If this field is blank, the widget can be added to any domain. ", -1)),
                      _createElementVNode("div", _hoisted_25, [
                        _createVNode(TagsInput, {
                          placeholder: "Enter a list of domains...",
                          modelValue: viewModel.value.whitelistedDomains,
                          "onUpdate:modelValue": _cache[18] || (_cache[18] = ($event: any) => ((viewModel.value.whitelistedDomains) = $event))
                        }, null, 8, ["modelValue"])
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_26, [
                      _cache[45] || (_cache[45] = _createElementVNode("label", { class: "label" }, "Identity Verification Secret", -1)),
                      _createVNode(ReadonlyCopyableField, {
                        readonlyValue: identityVerificationSecret.value,
                        onCopyMessage: "Copied snippet to clipboard"
                      }, null, 8, ["readonlyValue"]),
                      _cache[46] || (_cache[46] = _createElementVNode("p", { class: "help" }, " Use this secret to sign the JWT when verifying your users with us. This should only be done server side, so this value should never be exposed to front end applications. ", -1))
                    ])
                  ]),
                  _: 1
                }, 8, ["modelValue"]),
                _createVNode(_component_n_button, {
                  onClick: update,
                  variant: "primary",
                  loading: updateInProgress.value,
                  disabled: _unref($v).$invalid
                }, {
                  default: _withCtx(() => _cache[47] || (_cache[47] = [
                    _createTextVNode(" Save ")
                  ])),
                  _: 1
                }, 8, ["loading", "disabled"])
              ]),
              _createElementVNode("div", _hoisted_27, [
                _createElementVNode("div", _hoisted_28, [
                  _createElementVNode("div", _hoisted_29, [
                    _createVNode(WebAssistantViewToggle, {
                      activeView: activeView.value,
                      onViewChange: _cache[20] || (_cache[20] = (view) => (activeView.value = view))
                    }, null, 8, ["activeView"])
                  ]),
                  _createVNode(WebAssistantPreview, {
                    brandColour: viewModel.value.theme.brandColour,
                    outboundMessageColour: viewModel.value.theme.sentMessageColour,
                    actionColour: viewModel.value.theme.actionColour,
                    headerText: viewModel.value.headerText,
                    homescreenImageUrl: viewModel.value.imageUrl,
                    greetingText: viewModel.value.greetingText,
                    introductionText: viewModel.value.introductionText,
                    typicalResponseTime: viewModel.value.typicalResponseTime,
                    displayLauncher: viewModel.value.useDefaultLauncher,
                    homescreenApps: initialisedHomescreenApps.value,
                    isOpen: true,
                    messages: sampleMessages,
                    activeView: activeView.value,
                    disableInteraction: true,
                    onLoad: messageLoad
                  }, null, 8, ["brandColour", "outboundMessageColour", "actionColour", "headerText", "homescreenImageUrl", "greetingText", "introductionText", "typicalResponseTime", "displayLauncher", "homescreenApps", "activeView"])
                ])
              ])
            ])
          ])
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})